import React, { useMemo, useState, useEffect, useRef } from "react";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import Spinner from "./Spinner";

// Configure worker to match installed pdfjs-dist version
GlobalWorkerOptions.workerSrc =
  "https://unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs";

const norm = (s = "") =>
  s
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[^\w\s]/g, "")
    .trim();

const isBlankPage = (page) => {
  const textLen = (page.lines || []).reduce((sum, l) => sum + (l.text || "").length, 0);
  return (page.lines || []).length === 0 || textLen < 20;
};

async function extractPdfLines(file) {
  const buf = await file.arrayBuffer();
  const pdf = await getDocument({ data: buf, disableWorker: true }).promise;

  const pages = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const viewport = page.getViewport({ scale: 1.5 });
    const textContent = await page.getTextContent();
    const linesMap = new Map();

    textContent.items.forEach((item) => {
      const [, b, , d, e, f] = item.transform;
      const x = e;
      const y = viewport.height - f;
      const fontHeight = Math.hypot(b, d);
      const key = Math.round(y);
      const line = linesMap.get(key) || {
        items: [],
        yTop: y - fontHeight,
        yBottom: y,
      };
      line.items.push({ x, width: item.width, height: fontHeight, str: item.str });
      line.yTop = Math.min(line.yTop, y - fontHeight);
      line.yBottom = Math.max(line.yBottom, y);
      linesMap.set(key, line);
    });

    const lines = Array.from(linesMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([key, line], idx) => {
        const text = line.items
          .sort((a, b) => a.x - b.x)
          .map((i) => i.str || "")
          .join(" ");
        return {
          page: p,
          line: idx + 1,
          text,
          norm: norm(text),
          x: Math.min(...line.items.map((i) => i.x)),
          y: line.yTop,
          width: Math.max(...line.items.map((i) => i.x + i.width)) - Math.min(...line.items.map((i) => i.x)),
          height: line.yBottom - line.yTop,
          docLine:
            (text.match(/^\s*[^\da-zA-Z]?(\d{1,4})[.)]?\b/) ||
              text.match(/(\d{1,4})\s*$/))?.[1] || null,
        };
      });

    // Render once here so the image matches the coordinates exactly
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    const image = canvas.toDataURL();

    pages.push({ page: p, lines, width: viewport.width, height: viewport.height, image });
  }

  return pages;
}

function diffLinesSimple(a, b) {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? 1 + dp[i + 1][j + 1] : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0,
    j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      ops.push({ type: "equal", aIdx: i, bIdx: j });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ type: "remove", aIdx: i });
      i++;
    } else {
      ops.push({ type: "add", bIdx: j });
      j++;
    }
  }
  while (i < m) {
    ops.push({ type: "remove", aIdx: i });
    i++;
  }
  while (j < n) {
    ops.push({ type: "add", bIdx: j });
    j++;
  }
  return ops;
}

function buildChanges(templateFlat, contractFlat) {
  const aNorm = templateFlat.map((l) => l.norm);
  const bNorm = contractFlat.map((l) => l.norm);
  const ops = diffLinesSimple(aNorm, bNorm);
  const blocks = [];
  let cursor = 0;
  while (cursor < ops.length) {
    if (ops[cursor].type === "equal") {
      cursor++;
      continue;
    }
    const block = { removes: [], adds: [] };
    while (cursor < ops.length && ops[cursor].type !== "equal") {
      const op = ops[cursor];
      if (op.type === "remove") block.removes.push(templateFlat[op.aIdx]);
      if (op.type === "add") block.adds.push(contractFlat[op.bIdx]);
      cursor++;
    }
    blocks.push(block);
  }
  return blocks;
}

export default function PdfDiffLab() {
  const { user } = useAuth();
  const [templateFile, setTemplateFile] = useState(null);
  const [contractFile, setContractFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectingTemplate, setSelectingTemplate] = useState(false);
  const [templateMode, setTemplateMode] = useState("auto"); // auto | manual-upload | select-existing
  const [progress, setProgress] = useState(0);
  const [note, setNote] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const contractInputRef = useRef(null);

  const normalizeName = (name = "") => name.toLowerCase().replace(/\.pdf$/i, "").trim();

  useEffect(() => {
    const loadTemplates = async () => {
      if (!user) return;
      setLoadingTemplates(true);
      try {
        const { data, error: tplErr } = await supabase
          .from("contract_templates")
          .select("*")
          .order("created_at", { ascending: false });
        if (tplErr) throw tplErr;
        setTemplates(data || []);
      } catch (e) {
        setError(e?.message || "Template fetch failed");
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, [user]);

  const canRun = useMemo(() => !!templateFile && !!contractFile && !running, [templateFile, contractFile, running]);

  const filteredTemplates = useMemo(() => {
    const needle = templateSearch.toLowerCase().trim();
    if (!needle) return templates;
    return templates.filter((tpl) => (tpl.name || "").toLowerCase().includes(needle));
  }, [templateSearch, templates]);

  const splitIntoDocs = (pages) => {
    if (!pages.length) return [];
    const docs = [];
    let current = [];
    pages.forEach((p) => {
      if (isBlankPage(p)) {
        if (current.length) {
          docs.push(current);
          current = [];
        }
      } else {
        current.push(p);
      }
    });
    if (current.length) docs.push(current);
    return docs.length ? docs : [pages];
  };

  // Auto-pull template by matching filename when a contract is selected
  useEffect(() => {
    if (!contractFile || !templates.length || selectingTemplate) return;
    const contractBase = normalizeName(contractFile.name);
    if (!contractBase) return;
    // if already set and matches, skip
    if (templateFile && normalizeName(templateFile.name) === contractBase) return;
    const match = templates.find((tpl) => normalizeName(tpl.name) === contractBase);
    if (match) {
      fromBucket(match);
    }
  }, [contractFile, templates, templateFile, selectingTemplate]);

  const fromBucket = async (tpl) => {
    setSelectingTemplate(true);
    setError("");
    try {
      const { data, error: dlErr } = await supabase.storage
        .from("contract_templates")
        .download(tpl.object_name);
      if (dlErr) throw dlErr;
      const blob = data;
      const file = new File([blob], `${tpl.name || "template"}.pdf`, { type: blob.type || "application/pdf" });
      setTemplateFile(file);
    } catch (e) {
      setError(e?.message || "Template download failed");
    } finally {
      setSelectingTemplate(false);
    }
  };

  const runDiff = async () => {
    if (!contractFile) {
      setError("Please upload a contract PDF.");
      return;
    }
    // If manual or select-existing, require template; if auto and missing, try lookup now
    if (!templateFile) {
      if (templateMode === "auto" && templates.length) {
        const contractBase = normalizeName(contractFile.name);
        const match = templates.find((tpl) => normalizeName(tpl.name) === contractBase);
        if (match) {
          await fromBucket(match);
        }
      }
      if (!templateFile) {
        setError("Please provide a template (auto match failed).");
        return;
      }
    }
    if (!canRun) return;
    setRunning(true);
    setProgress(10);
    setNote("Reading PDFs…");
    setError("");
    try {
      const [tplPages, ctrPages] = await Promise.all([extractPdfLines(templateFile), extractPdfLines(contractFile)]);
      setProgress(60);
      setNote("Computing differences…");
      const templateFlat = tplPages.flatMap((p) => p.lines.map((l) => ({ ...l, page: p.page })));
      const docGroups = splitIntoDocs(ctrPages);
      const docResults = docGroups.map((pages, idx) => {
        const contractFlat = pages.flatMap((p) => p.lines.map((l) => ({ ...l, page: p.page })));
        const changes = buildChanges(templateFlat, contractFlat);
        return {
          index: idx + 1,
          startPage: pages[0]?.page,
          endPage: pages[pages.length - 1]?.page,
          changes,
        };
      });

      setProgress(90);
      setNote("Preparing results…");
      setResult({
        pages: { template: tplPages.length, contract: ctrPages.length },
        docs: docResults,
        tplPages,
        ctrPages,
        tplPreview: URL.createObjectURL(templateFile),
        ctrPreview: URL.createObjectURL(contractFile),
      });
      setProgress(100);
      setNote("Done");
    } catch (e) {
      setError(e?.message || "Diff failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="page">
      <div className="card tool">
        <h1 className="title">Contract Hero</h1>
        <p className="subtitle">
          Automatically detect contract types and compare against master templates to identify unauthorized changes
        </p>

        <div className="grid two">
          <div className="field">
            <span className="field-label-strong">Uploaded PDF</span>
            <div
              className={`drop ${dragOver ? "dragging" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file?.type === "application/pdf") {
                  setContractFile(file);
                }
              }}
              onClick={(e) => {
                if (e.target !== e.currentTarget) return;
                contractInputRef.current?.click();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  contractInputRef.current?.click();
                }
              }}
            >
              <div className="drop-icon">📄</div>
              <p className="drop-text">Drag & drop your PDF here</p>
              <p className="hint">or click to choose a file</p>
              <input
                ref={contractInputRef}
                type="file"
                accept="application/pdf"
                className="file-input"
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setContractFile(e.target.files?.[0] || null)}
              />
            </div>
            {contractFile && <div className="muted small">{contractFile.name}</div>}
          </div>
          <div className="field">
            <span className="field-label-strong">Template mode</span>
            <div className="buttons">
              <button
                type="button"
                className={`ghost-btn ${templateMode === "auto" ? "selected" : ""}`}
                onClick={() => setTemplateMode("auto")}
              >
                Auto by name
              </button>
              <button
                type="button"
                className={`ghost-btn ${templateMode === "manual-upload" ? "selected" : ""}`}
                onClick={() => setTemplateMode("manual-upload")}
              >
                Upload template
              </button>
              <button
                type="button"
                className={`ghost-btn ${templateMode === "select-existing" ? "selected" : ""}`}
                onClick={() => setTemplateMode("select-existing")}
              >
                Select stored
              </button>
            </div>

            {templateMode === "manual-upload" && (
              <div style={{ marginTop: 8 }}>
                <label className="file-btn">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setTemplateFile(e.target.files?.[0] || null)}
                  />
                  <span>{templateFile ? "Change template" : "Choose template"}</span>
                </label>
                {templateFile && <div className="muted small">{templateFile.name}</div>}
              </div>
            )}

            {templateMode === "select-existing" && templates.length > 0 && (
              <div className="template-picker" style={{ marginTop: 8 }}>
                <div className="template-table-head">
                  <input
                    className="template-search"
                    placeholder="Search templates"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    disabled={selectingTemplate || loadingTemplates}
                  />
                  <div className="template-count">
                    {filteredTemplates.length} Document(s)
                  </div>
                </div>
                  <div className="template-table">
                    <div className="template-row header">
                      <span>Name</span>
                      <span>Size</span>
                      <span>Date</span>
                    </div>
                    {filteredTemplates.map((tpl) => (
                      <div
                        key={tpl.id}
                        className="template-row clickable"
                        onClick={() => !selectingTemplate && fromBucket(tpl)}
                      >
                        <span>{tpl.name}</span>
                        <span>{tpl.file_size ? `${Math.round(tpl.file_size / 1024)} KB` : ""}</span>
                        <span>{tpl.created_at ? new Date(tpl.created_at).toLocaleDateString() : ""}</span>
                      </div>
                    ))}
                    {filteredTemplates.length === 0 && (
                      <div className="template-row muted">
                        <span>No templates found</span>
                        <span />
                      <span />
                    </div>
                  )}
                </div>
                {selectingTemplate && <div className="muted small">Downloading…</div>}
              </div>
            )}

            {templateMode === "auto" && (
              <div className="muted small" style={{ marginTop: 8 }}>
                Automatically match stored template by filename. If no match is found, switch to another mode.
              </div>
            )}
          </div>
        </div>

        {running && (
          <>
            <div className="progress-wrap">
              <div className="progress-bar" style={{ width: `${progress}%` }} />
            </div>
            <div className="status-row">
              <span className="status-note">
                <Spinner /> <span className="note-text">{note}</span>
              </span>
              <span className="tag running">{Math.round(progress)}%</span>
            </div>
          </>
        )}

        <div className="buttons">
          <button className={`primary ${!canRun ? "disabled" : ""}`} disabled={!canRun} onClick={runDiff}>
            {running ? "Analyzing…" : "Validate document"}
          </button>
          <button
            className="ghost-btn"
            type="button"
            onClick={() => {
              setTemplateFile(null);
              setContractFile(null);
              setResult(null);
              setError("");
              setProgress(0);
              setNote("");
            }}
          >
            Reset
          </button>
        </div>

        {error && <div className="error-status">⚠️ {error}</div>}

        {result && (
          <div className="diff-lab-results">
            <h3>Detected changes</h3>
            {(!result.docs || result.docs.length === 0) && <div className="muted">No differences found.</div>}
            {result.docs?.map((doc) => (
              <div key={doc.index} className="doc-block">
                <div className="doc-header">
                  <div className="doc-title">
                    Document {doc.index} {doc.startPage && doc.endPage ? `(Pages ${doc.startPage}-${doc.endPage})` : ""}
                  </div>
                  <div className="doc-count">{doc.changes.length} change(s)</div>
                </div>
                {doc.changes.length === 0 ? (
                  <div className="muted small">No differences found in this segment.</div>
                ) : (
                  doc.changes.map((block, idx) => (
                    <div key={idx} className="change-item change-modification">
                      <div className="change-header">
                        <span className="change-type">Change #{idx + 1}</span>
                        <span className="change-severity">INFO</span>
                      </div>
                      <div className="change-content">
                        <div className="change-line original">
                          <span className="line-label">Original Contract</span>
                          {block.removes.length === 0 ? (
                            <span className="muted small">No matching lines</span>
                          ) : (
                            block.removes.map((l, li) => (
                              <pre key={li} className="line-text">
                                [Page {l.page} Line {l.docLine || l.line}] {l.text}
                              </pre>
                            ))
                          )}
                        </div>
                        <div className="change-line uploaded">
                          <span className="line-label">Uploaded Contract</span>
                          {block.adds.length === 0 ? (
                            <span className="muted small">No matching lines</span>
                          ) : (
                            block.adds.map((l, li) => (
                              <pre key={li} className="line-text">
                                [Page {l.page} Line {l.docLine || l.line}] {l.text}
                              </pre>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ))}

            <h3>Full PDFs (scrollable)</h3>
            <details className="pdf-collapse">
              <summary>Template ({templateFile?.name || "selected template"})</summary>
              <div className="pdf-embed">
                <object data={result.tplPreview} type="application/pdf" width="100%" height="600px">
                  <p>
                    PDF preview unavailable. <a href={result.tplPreview}>Download</a>
                  </p>
                </object>
              </div>
            </details>
            <details className="pdf-collapse">
              <summary>Uploaded ({contractFile?.name || "uploaded PDF"})</summary>
              <div className="pdf-embed">
                <object data={result.ctrPreview} type="application/pdf" width="100%" height="600px">
                  <p>
                    PDF preview unavailable. <a href={result.ctrPreview}>Download</a>
                  </p>
                </object>
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Modals removed as requested */}
    </div>
  );
}
